import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Chip,
  Avatar,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
} from '@mui/material';
import {
  ArrowBack,
  Assignment,
  Info,
  Receipt,
  ContactSupport,
  Devices,
  History,
  CheckCircle,
  Block,
  Article,
  NetworkCheck,
  Verified,
  Hub,
  Build,
  PauseCircle,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';

import { useAuth, usePermissions } from '../contexts/AuthContext';
import { contractService } from '../services/contractService';
import { Contract } from '../types';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { formatarData, formatarValor } from '../utils/formatToView';
import { getStatusColor, getStatusLabel, getStatusIcon } from '../utils/contractStatusUtils';
import { detailPageHeroPaperSx, detailPageTabsSx } from '../styles/theme';
import ContractAlteracaoWizard from '../components/contract/ContractAlteracaoWizard';
// Componentes das abas
import {
  ContractOverview,
  ContractInvoices,
  ContractTickets,
  ContractEquipments,
  ContractLogs,
  ContractLayout,
  ContractConnections,
  ContractServicos,
} from '../components/contract';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contract-tabpanel-${index}`}
      aria-labelledby={`contract-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `contract-tab-${index}`,
    'aria-controls': `contract-tabpanel-${index}`,
  };
}

const ContractDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { can } = usePermissions();
  const theme = useTheme();
  const podeEditar = can('contratos.editar');
  const podeBloquear = can('contratos.bloquear');
  const podeDesbloquear = can('contratos.desbloquear');
  const podeDesbloquearConfianca = can('contratos.desbloquear_confianca');
  const podeCancelar = can('contratos.cancelar');
  const podeAbrirWizard = podeEditar || podeCancelar;

  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [blockLoading, setBlockLoading] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [trustUnblockLoading, setTrustUnblockLoading] = useState(false);
  const [trustUnblockDialogOpen, setTrustUnblockDialogOpen] = useState(false);
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const [alteracaoWizardOpen, setAlteracaoWizardOpen] = useState(false);

  // Estados para modal de suspensão
  const [suspensaoModalOpen, setSuspensaoModalOpen] = useState(false);
  const [suspensaoLoading, setSuspensaoLoading] = useState(false);
  const [suspensaoForm, setSuspensaoForm] = useState({
    suspensao_inicio: new Date().toISOString().substring(0, 10),
    suspensao_fim: '',
    suspensao_motivo: '' as string,
    observacao: '',
  });

  useEffect(() => {
    if (id) {
      loadContract(parseInt(id));
    }
  }, [id]);

  // Effect para definir a aba inicial baseada na URL
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const tabMap: { [key: string]: number } = {
        'overview': 0,
        'contrato-servicos': 1,
        'invoices': 2,
        'tickets': 3,
        'equipments': 4,
        'connections': 5,
        'logs': 6,
        'layout': 7,
      };

      const tabIndex = tabMap[tabParam];
      if (tabIndex !== undefined) {
        setTabValue(tabIndex);
      }
    }
  }, [searchParams]);

  const loadContract = async (contractId: number) => {
    try {
      setLoading(true);
      setError(null);

      // Usar dados reais da API
      const response = await contractService.getById(contractId);

      if (response.success && response.data) {
        setContract(response.data);
      } else {
        const errorMessage = response.message || 'Contrato não encontrado';
        setError(errorMessage);
      }
    } catch (error: any) {
      setError('Erro ao carregar contrato: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleBack = () => {
    if (contract?.cliente_id) {
      navigate(`/clients/${contract.cliente_id}`);
    } else {
      navigate('/clients');
    }
  };

  const handleAtivarContract = async () => {
    if (!contract) return;

    const result = await Swal.fire({
      title: 'Ativar Contrato',
      text: `Tem certeza que deseja ativar o contrato "${contract?.titulo}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, ativar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
    });
    if (result.isConfirmed) {
      const response = await contractService.ativar(contract.id);
      if (response.success) {
        toast.success('Contrato ativado com sucesso!');
        if (id) {
          loadContract(parseInt(id));
        }
      } else {
        toast.error(response.message || 'Erro ao ativar contrato');
      }
    }
  };

  const handleBlockContract = () => {
    setBlockDialogOpen(true);
  };

  const handleBlockDialogClose = () => {
    setBlockDialogOpen(false);
  };

  const handleConfirmBlock = async () => {
    if (!contract) return;

    try {
      setBlockLoading(true);

      const response = contract.status === 'bloqueado'
        ? await contractService.desbloquear(contract.id)
        : await contractService.bloquear(contract.id);

      if (response.success) {
        setBlockDialogOpen(false);
        // Recarregar os dados do contrato
        if (id) {
          loadContract(parseInt(id));
        }
        toast.success(
          contract.status === 'bloqueado'
            ? 'Contrato desbloqueado com sucesso!'
            : 'Contrato bloqueado com sucesso!'
        );
      } else {
        toast.error(response.message || 'Erro ao alterar status do contrato');
      }
    } catch (err: any) {
      console.error('Erro ao alterar status do contrato:', err);
      toast.error('Erro ao alterar status do contrato');
    } finally {
      setBlockLoading(false);
    }
  };

  const handleTrustUnblock = () => {
    setTrustUnblockDialogOpen(true);
    setShowCustomDate(false);
    setCustomDate('');
  };

  const handleTrustUnblockDialogClose = () => {
    setTrustUnblockDialogOpen(false);
    setShowCustomDate(false);
    setCustomDate('');
  };

  const handleConfirmTrustUnblock = async () => {
    if (!contract) return;

    // Validar data personalizada se fornecida
    if (showCustomDate && customDate) {
      const selectedDate = new Date(customDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        toast.error('A data de desbloqueio não pode ser anterior à data atual');
        return;
      }
    }

    try {
      setTrustUnblockLoading(true);

      const response = await contractService.desbloquearConfianca(
        contract.id,
        showCustomDate && customDate ? customDate : undefined
      );
      if (response.success) {
        setTrustUnblockDialogOpen(false);
        // Recarregar os dados do contrato
        if (id) {
          loadContract(parseInt(id));
        }
        toast.success(response.message || 'Contrato desbloqueado em confiança com sucesso!');
      } else {
        toast.error(response.message || 'Erro ao desbloquear contrato em confiança');
      }
    } catch (err: any) {
      console.error('Erro ao desbloquear contrato em confiança:', err);
      toast.error('Erro ao desbloquear contrato em confiança');
    } finally {
      setTrustUnblockLoading(false);
    }
  };

  const podeToggleBloqueio = contract?.status === 'bloqueado' ? podeDesbloquear : podeBloquear;

  // Handlers de Suspensão
  const handleOpenSuspensao = () => {
    setSuspensaoForm({
      suspensao_inicio: new Date().toISOString().substring(0, 10),
      suspensao_fim: '',
      suspensao_motivo: '',
      observacao: '',
    });
    setSuspensaoModalOpen(true);
  };

  const handleSuspensaoSubmit = async () => {
    if (!contract) return;

    if (!suspensaoForm.suspensao_inicio) {
      toast.error('Data de início é obrigatória');
      return;
    }
    if (!suspensaoForm.suspensao_motivo) {
      toast.error('Motivo é obrigatório');
      return;
    }

    try {
      setSuspensaoLoading(true);
      const response = await contractService.suspender(
        contract.id,
        {
          suspensao_inicio: suspensaoForm.suspensao_inicio,
          suspensao_fim: suspensaoForm.suspensao_fim || null,
          suspensao_motivo: suspensaoForm.suspensao_motivo,
          observacao: suspensaoForm.observacao || undefined,
        }
      );

      if (response.success) {
        toast.success(response.message || 'Contrato suspenso com sucesso!');
        setSuspensaoModalOpen(false);
        // Recarregar dados do contrato
        if (id) loadContract(parseInt(id));
      } else {
        toast.error(response.message || 'Erro ao suspender contrato');
      }
    } catch (err: any) {
      console.error('Erro ao suspender contrato:', err);
      toast.error(err.response?.data?.message || 'Erro ao suspender contrato');
    } finally {
      setSuspensaoLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center">
          <CircularProgress size={60} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Carregando dados do contrato...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error || !contract) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Contrato não encontrado'}
        </Alert>
        <Button onClick={handleBack} startIcon={<ArrowBack />}>
          Voltar
        </Button>
      </Box>
    );
  }

  // Validação adicional dos dados do contrato
  if (!contract.id || !contract.titulo) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Os dados do contrato estão incompletos. Por favor, entre em contato com o suporte.
        </Alert>
        <Button onClick={handleBack} startIcon={<ArrowBack />}>
          Voltar
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{contract.titulo} - Detalhes do Contrato - SysUp</title>
      </Helmet>

      <Box>

        {/* Header do Contrato - Redesenhado */}
        <Paper
          elevation={2}
          sx={detailPageHeroPaperSx(theme)}
        >
          {/* Seção Principal do Header */}
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              borderBottom: 1,
              borderColor: 'divider'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                gap: { xs: 2, md: 3 }
              }}
            >
              {/* Informações Principais */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flex: 1,
                  minWidth: 0
                }}
              >
                <IconButton
                  onClick={handleBack}
                  color="primary"
                  sx={{
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': {
                      bgcolor: 'action.hover',
                      transform: 'translateY(-1px)',
                      boxShadow: 2
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <ArrowBack />
                </IconButton>

                {window.innerWidth > 768 && (
                  <Avatar
                    sx={{
                      width: { xs: 40, md: 64 },
                      height: { xs: 40, md: 64 },
                      bgcolor: 'primary.main',
                      boxShadow: 2
                    }}
                  >
                    <Assignment fontSize={window.innerWidth < 768 ? "medium" : "large"} />
                  </Avatar>
                )}

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{
                      color: 'text.primary',
                      lineHeight: 1.2,
                      wordBreak: 'break-word',
                      fontSize: { xs: '1.25rem', md: '2rem' }
                    }}
                  >
                    {contract.titulo}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                      fontSize: { xs: '0.875rem', md: '1rem' }
                    }}
                  >
                    Contrato #{contract.id} • {contract.cliente?.nome || 'Cliente não encontrado'}
                  </Typography>
                </Box>
              </Box>



              {/* Status e Ações */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'row', md: 'row' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  width: { xs: '100%', md: 'auto' }
                }}
              >
                <Chip
                  label={getStatusLabel(contract.status)}
                  color={getStatusColor(contract.status) as any}
                  icon={getStatusIcon(contract.status, 'small')}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    boxShadow: 1
                  }}
                />

                {(podeAbrirWizard || podeToggleBloqueio || podeDesbloquearConfianca || podeEditar) && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {podeAbrirWizard && contract.status !== 'cancelado' && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        startIcon={<Build />}
                        onClick={() => setAlteracaoWizardOpen(true)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          boxShadow: 1,
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: 2
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Alteração Wizard
                      </Button>
                    )}

                    {!['cancelado', 'suspenso'].includes(contract.status) && (
                      <>
                        {podeToggleBloqueio && (
                          <Button
                            variant="outlined"
                            color={contract.status === 'bloqueado' ? 'success' : 'warning'}
                            size="small"
                            startIcon={contract.status === 'bloqueado' ? <CheckCircle /> : <Block />}
                            onClick={handleBlockContract}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 500,
                              boxShadow: 1,
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: 2
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {contract.status === 'bloqueado' ? 'Desbloquear' : 'Bloquear'}
                          </Button>
                        )}
                        {podeDesbloquearConfianca && contract.status !== 'aguardando_instalacao' && contract.status !== 'aguardando_assinatura' && (
                          <Button
                            variant="outlined"
                            color="info"
                            size="small"
                            startIcon={<Verified />}
                            onClick={handleTrustUnblock}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 500,
                              boxShadow: 1,
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: 2
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            Desbloqueio em Confiança
                          </Button>
                        )}
                      </>
                    )}

                    {podeEditar && !['cancelado', 'suspenso'].includes(contract.status) && (
                      <Button
                        variant="outlined"
                        color="warning"
                        size="small"
                        startIcon={<PauseCircle />}
                        onClick={handleOpenSuspensao}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          boxShadow: 1,
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: 2
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Suspender
                      </Button>
                    )}
                    {podeEditar && ['cancelado', 'suspenso'].includes(contract.status) && (
                      <Button
                        variant="outlined"
                        color="success"
                        size="small"
                        startIcon={<CheckCircle />}
                        onClick={handleAtivarContract}
                      >
                        Ativar
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Informações Rápidas - Cards Compactos */}
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={{ xs: 1.5, md: 2 }}>
              <Grid item xs={6} sm={2.4}>
                <Box
                  sx={{
                    p: { xs: 1, md: 1 },
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    boxShadow: 1,
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 2
                    }
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    Data Contrato
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ mt: 0.5, color: 'text.primary' }}
                  >
                    {formatarData(contract.data_contrato)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} sm={2.4}>
                <Box
                  sx={{
                    p: { xs: 1, md: 1 },
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    boxShadow: 1,
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 2
                    }
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    Data Instalação
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ mt: 0.5, color: 'text.primary' }}
                  >
                    {contract.data_instalacao ? formatarData(contract.data_instalacao) : 'Não informada'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} sm={2.4}>
                <Box
                  sx={{
                    p: { xs: 1, md: 1 },
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    boxShadow: 1,
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 2
                    }
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    Vencimento
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ mt: 0.5, color: 'text.primary' }}
                  >
                    Dia {contract.dia_vencimento}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} sm={2.4}>
                <Box
                  sx={{
                    p: { xs: 1, md: 1 },
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    boxShadow: 1,
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 2
                    }
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    Fidelidade
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ mt: 0.5, color: 'text.primary' }}
                  >
                    {contract.fidelidade} meses
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} sm={2.4}>
                <Box
                  sx={{
                    p: { xs: 1, md: 1 },
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    boxShadow: 1,
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 2
                    }
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    Valor do Contrato
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ mt: 0.5, color: 'text.primary' }}
                  >
                    {formatarValor(contract.valor_contratado)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Abas */}
        <Paper elevation={2} sx={{ borderRadius: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="Abas de Contrato"
              sx={detailPageTabsSx(theme)}
            >
              <Tab
                label="Dados"
                icon={<Info sx={{ fontSize: 18 }} />}
                iconPosition="start"
                {...a11yProps(0)}
              />
              <Tab
                label="Serviços"
                icon={<Hub sx={{ fontSize: 18 }} />}
                iconPosition="start"
                {...a11yProps(1)}
              />
              <Tab
                label="Mensalidades"
                icon={<Receipt sx={{ fontSize: 18 }} />}
                iconPosition="start"
                {...a11yProps(2)}
              />
              <Tab
                label="Chamados"
                icon={<ContactSupport sx={{ fontSize: 18 }} />}
                iconPosition="start"
                {...a11yProps(3)}
              />
              <Tab
                label="Equipamentos"
                icon={<Devices sx={{ fontSize: 18 }} />}
                iconPosition="start"
                {...a11yProps(4)}
              />
              <Tab
                label="Conexões"
                icon={<NetworkCheck sx={{ fontSize: 18 }} />}
                iconPosition="start"
                {...a11yProps(5)}
              />
              <Tab
                label="Logs"
                icon={<History sx={{ fontSize: 18 }} />}
                iconPosition="start"
                {...a11yProps(6)}
              />
              <Tab
                label="Layout"
                icon={<Article sx={{ fontSize: 18 }} />}
                iconPosition="start"
                {...a11yProps(7)}
              />
            </Tabs>
          </Box>

          {/* Conteúdo das Abas */}
          <TabPanel value={tabValue} index={0}>
            <ContractOverview
              contract={contract}
              onRefresh={() => loadContract(contract.id)}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <ContractServicos contractId={contract.id} contractStatus={contract.status} dadosContrato={contract} />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <ContractInvoices contractId={contract.id} clientId={contract.cliente_id} contractStatus={contract.status} />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <ContractTickets contractId={contract.id} contractStatus={contract.status} />
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <ContractEquipments contractId={contract.id} contractStatus={contract.status} />
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            <ContractConnections contractId={contract.id} />
          </TabPanel>

          <TabPanel value={tabValue} index={6}>
            <ContractLogs contractId={contract.id} />
          </TabPanel>

          <TabPanel value={tabValue} index={7}>
            <ContractLayout contractId={contract.id} contract={contract} contractStatus={contract.status} />
          </TabPanel>
        </Paper>
      </Box>

      {/* Modal de Confirmação de Bloqueio/Desbloqueio */}
      <Dialog
        open={blockDialogOpen}
        onClose={handleBlockDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {contract?.status === 'bloqueado' ? (
            <CheckCircle color="success" />
          ) : (
            <Block color="warning" />
          )}
          {contract?.status === 'bloqueado' ? 'Desbloquear' : 'Bloquear'} Contrato
        </DialogTitle>

        <DialogContent>
          <Alert severity={contract?.status === 'bloqueado' ? 'info' : 'warning'} sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Confirmação:</strong> Esta ação alterará o status do contrato.
            </Typography>
          </Alert>

          <Typography variant="body1" sx={{ mb: 2 }}>
            Tem certeza que deseja {contract?.status === 'bloqueado' ? 'desbloquear' : 'bloquear'} o contrato <strong>"{contract?.titulo}"</strong>?
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Ao {contract?.status === 'bloqueado' ? 'desbloquear' : 'bloquear'} este contrato:
          </Typography>
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            <li>
              <Typography variant="body2" color="text.secondary">
                O status será alterado para "{contract?.status === 'bloqueado' ? 'Ativo' : 'Bloqueado'}"
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                O Desbloqueio em confiança será removido
              </Typography>
            </li>
            {contract?.status === 'bloqueado' ? (
              <li>
                <Typography variant="body2" color="text.secondary">
                  A data de bloqueio será removida
                </Typography>
              </li>
            ) : (
              <li>
                <Typography variant="body2" color="text.secondary">
                  A data de bloqueio será registrada como hoje ({new Date().toLocaleDateString('pt-BR')})
                </Typography>
              </li>
            )}
            <li>
              <Typography variant="body2" color="text.secondary">
                {contract?.status === 'bloqueado'
                  ? 'O cliente voltará a ter acesso aos serviços'
                  : 'O cliente perderá o acesso aos serviços'}
              </Typography>
            </li>
          </ul>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleBlockDialogClose}
            disabled={blockLoading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={contract?.status === 'bloqueado' ? 'success' : 'warning'}
            onClick={handleConfirmBlock}
            disabled={blockLoading}
            startIcon={blockLoading ? <CircularProgress size={20} /> :
              (contract?.status === 'bloqueado' ? <CheckCircle /> : <Block />)}
          >
            {blockLoading ?
              (contract?.status === 'bloqueado' ? 'Desbloqueando...' : 'Bloqueando...') :
              (contract?.status === 'bloqueado' ? 'Confirmar Desbloqueio' : 'Confirmar Bloqueio')}
          </Button>
        </DialogActions>
      </Dialog>

      <ContractAlteracaoWizard
        open={alteracaoWizardOpen}
        onClose={() => setAlteracaoWizardOpen(false)}
        contract={contract}
        onRefresh={() => loadContract(contract.id)}
      />

      {/* Modal de Confirmação de Desbloqueio em Confiança */}
      <Dialog
        open={trustUnblockDialogOpen}
        onClose={handleTrustUnblockDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Verified color="info" />
          Desbloqueio em Confiança
        </DialogTitle>

        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Desbloqueio Temporário:</strong> O contrato será desbloqueado por um período limitado.
            </Typography>
          </Alert>


          {/* Botão Alterar Data */}
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowCustomDate(!showCustomDate)}
              sx={{ mb: 2 }}
            >
              {showCustomDate ? 'Usar Data Automática' : 'Alterar Data'}
            </Button>

            {/* Input de Data Personalizada */}
            {showCustomDate && (
              <TextField
                type="date"
                label="Desbloquear até"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Data mínima é amanhã
                  max: "2999-12-31"
                }}
                helperText="Selecione até quando o contrato ficará desbloqueado, no dia seguinte o contrato será bloqueado automaticamente"
                sx={{ mb: 2 }}
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleTrustUnblockDialogClose}
            disabled={trustUnblockLoading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={handleConfirmTrustUnblock}
            disabled={trustUnblockLoading || (showCustomDate && !customDate)}
            startIcon={trustUnblockLoading ? <CircularProgress size={20} /> : <Verified />}
          >
            {trustUnblockLoading ? 'Desbloqueando...' : 'Confirmar Desbloqueio em Confiança'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de erro removido - agora usando SweetAlert2 */}

      {/* Modal de Suspensão de Contrato */}
      <Dialog
        open={suspensaoModalOpen}
        onClose={() => !suspensaoLoading && setSuspensaoModalOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="suspensao-dialog-title"
      >
        <DialogTitle id="suspensao-dialog-title" sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <PauseCircle color="warning" />
            Suspender Contrato
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
            {/* Info do contrato */}
            <Alert severity="info" sx={{ py: 0.5 }}>
              Contrato: <strong>#{contract?.id} - {contract?.titulo}</strong>
            </Alert>

            <Alert severity="warning" sx={{ py: 0.5 }}>
              Ao suspender o contrato, <strong>não será gerada fatura automática</strong> para o período de suspensão e <strong>todos os serviços serão desligados</strong>.
            </Alert>

            {/* Data Início e Data Fim lado a lado */}
            <Box display="flex" gap={2}>
              <TextField
                label="Data Início"
                type="date"
                size="small"
                fullWidth
                required
                value={suspensaoForm.suspensao_inicio}
                onChange={(e) => setSuspensaoForm(prev => ({ ...prev, suspensao_inicio: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                disabled={suspensaoLoading}
              />
              <TextField
                label="Data Fim (opcional)"
                type="date"
                size="small"
                fullWidth
                value={suspensaoForm.suspensao_fim}
                onChange={(e) => setSuspensaoForm(prev => ({ ...prev, suspensao_fim: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                disabled={suspensaoLoading}
              />
            </Box>

            {/* Motivo */}
            <FormControl fullWidth size="small" required>
              <InputLabel>Motivo</InputLabel>
              <Select
                value={suspensaoForm.suspensao_motivo}
                label="Motivo"
                onChange={(e) => setSuspensaoForm(prev => ({ ...prev, suspensao_motivo: e.target.value as string }))}
                disabled={suspensaoLoading}
              >
                <MenuItem value="inadimplencia">Inadimplência</MenuItem>
                <MenuItem value="ferias">Férias</MenuItem>
                <MenuItem value="suspensao_temporaria">Suspensão Temporária</MenuItem>
              </Select>
            </FormControl>

            {/* Observação */}
            <TextField
              label="Observação"
              multiline
              rows={3}
              size="small"
              fullWidth
              value={suspensaoForm.observacao}
              onChange={(e) => setSuspensaoForm(prev => ({ ...prev, observacao: e.target.value }))}
              disabled={suspensaoLoading}
              placeholder="Observação sobre a suspensão (será adicionada ao campo de observações do contrato)"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setSuspensaoModalOpen(false)}
            disabled={suspensaoLoading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSuspensaoSubmit}
            disabled={suspensaoLoading}
            startIcon={suspensaoLoading ? <CircularProgress size={16} /> : <PauseCircle />}
          >
            {suspensaoLoading ? 'Suspendendo...' : 'Suspender'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContractDetailsPage; 